import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { HttpClientInterface } from '.';

export type QueryKey = readonly (string | number)[];

export type QueryFnParams = { client: IQueryClient; http: HttpClientInterface };

export interface IQueryClient {
  invalidate(key?: QueryKey): void;
  getQueryData<T>(key: QueryKey): T | undefined;
  setQueryData<T>(key: QueryKey, data: T): void;
  subscribe(key: QueryKey, listener: () => void): () => void;
  isStale(key: QueryKey, staleTime: number): boolean;
  fetch<T>(key: QueryKey, fetcher: (params: QueryFnParams) => Promise<T>): Promise<T>;
  execute<T>(task: (params: QueryFnParams) => Promise<T>): Promise<T>;
}

interface CacheEntry {
  data: unknown;
  updatedAt: number;
}

export class QueryClient implements IQueryClient {
  // All maps are keyed by JSON.stringify(QueryKey).
  private cache = new Map<string, CacheEntry>();
  private listeners = new Map<string, Set<() => void>>();
  private inFlight = new Map<string, Promise<unknown>>();
  private timeouts = new Map<string, ReturnType<typeof setTimeout>>();
  private gcTime = 5 * 60 * 1000;

  constructor(private http: HttpClientInterface) { }

  /**
   * Matches by prefix: ['sites'] hits ['sites','list'] and ['sites', uuid].
   * Ages entries out rather than deleting them, so observers keep rendering
   * stale data while the refetch is in flight.
   */
  invalidate(key?: QueryKey): void {
    this.cache.forEach((entry, sKey) => {
      const parsed = JSON.parse(sKey) as unknown[];
      if (!key || key.every((part, i) => parsed[i] === part)) {
        entry.updatedAt = 0;
        this.listeners.get(sKey)?.forEach(l => l());
      }
    });
  }

  getQueryData<T>(key: QueryKey): T | undefined {
    return this.cache.get(JSON.stringify(key))?.data as T | undefined;
  }

  setQueryData<T>(key: QueryKey, data: T): void {
    const sKey = JSON.stringify(key);
    this.cache.set(sKey, { data, updatedAt: Date.now() });
    this.listeners.get(sKey)?.forEach(l => l());
  }

  isStale(key: QueryKey, staleTime: number): boolean {
    const entry = this.cache.get(JSON.stringify(key));
    return !entry || Date.now() - entry.updatedAt > staleTime;
  }

  /**
   * Subscribing cancels any pending GC, so a component remounting within gcTime
   * reuses the cached data.
   */
  subscribe(key: QueryKey, listener: () => void) {
    const sKey = JSON.stringify(key);
    if (!this.listeners.has(sKey)) this.listeners.set(sKey, new Set());

    const pending = this.timeouts.get(sKey);
    if (pending !== undefined) {
      clearTimeout(pending);
      this.timeouts.delete(sKey);
    }

    this.listeners.get(sKey)!.add(listener);
    return () => {
      const set = this.listeners.get(sKey);
      set?.delete(listener);
      if (!set || set.size === 0) {
        const timeout = setTimeout(() => {
          this.cache.delete(sKey);
          this.timeouts.delete(sKey);
          this.listeners.delete(sKey);
        }, this.gcTime);
        this.timeouts.set(sKey, timeout);
      }
    };
  }

  /**
   * On rejection nothing is cached — readSnapshot treats "no entry" as the cue
   * to keep showing the error, so caching a failure would clear it and loop.
   *
   * setQueryData runs while every awaiting useQuery.fetch is still parked, so
   * their fetchingRef guards are true when listeners fire. That's what keeps two
   * observers of one key from ping-ponging refetches.
   */
  async fetch<T>(key: QueryKey, fetcher: (params: QueryFnParams) => Promise<T>): Promise<T> {
    const sKey = JSON.stringify(key);
    const existing = this.inFlight.get(sKey);
    if (existing) return existing as Promise<T>;

    const promise = fetcher({ client: this, http: this.http }).finally(() => this.inFlight.delete(sKey));
    this.inFlight.set(sKey, promise);
    const results = await promise;

    this.setQueryData(key, results);
    return results;
  }

  execute<T>(task: (params: QueryFnParams) => Promise<T>): Promise<T> {
    return task({ client: this, http: this.http });
  }
}

export const QueryContext = createContext<IQueryClient | undefined>(undefined);

export const useQueryClient = () => {
  const client = useContext(QueryContext);
  if (!client) throw new Error('QueryProvider missing');
  return client;
};

export interface QueryState<TData> {
  data: TData | null;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
}

/**
 * Pure and non-throwing; it only reads. Presence is tested against `undefined`,
 * so a cached `null`, `0`, `''` or `false` counts as loaded.
 *
 * A failed query is not loading, so callers gated only on `isLoading` will
 * render an empty page on error.
 */
function readSnapshot<T>(
  client: IQueryClient,
  key: QueryKey,
  isFetching: boolean,
  prevError: Error | null,
): QueryState<T> {
  const cached = client.getQueryData<T>(key);
  const hasData = cached !== undefined;
  const error = hasData ? null : prevError;

  return {
    data: hasData ? (cached as T) : null,
    isLoading: !hasData && error === null,
    isFetching,
    error,
  };
}

/**
 * `fetcher` must return finished domain objects — parse inside it, so a bad
 * payload throws into the catch below.
 */
export function useQuery<T>(
  queryKey: QueryKey,
  fetcher: (params: QueryFnParams) => Promise<T>,
  options: { staleTime?: number } = {}
) {
  const { staleTime = 0 } = options;
  const client = useQueryClient();

  // Identity derived from the serialized form, so it only changes when the
  // contents do — safe as a dep despite callers passing a fresh array literal.
  const sKey = JSON.stringify(queryKey);
  const key = useMemo(() => JSON.parse(sKey) as QueryKey, [sKey]);

  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  // Per-observer, not global: gates the refetch-on-notify path so an observer
  // doesn't react to its own cache write.
  const fetchingRef = useRef(false);
  // A resolving fetch whose id no longer matches is stale; its result is dropped.
  const fetchIdRef = useRef(0);

  const [state, setState] = useState<QueryState<T>>(() =>
    readSnapshot<T>(client, key, false, null),
  );

  // On a key change the existing state belongs to the previous key. Resync during
  // render so we never show another key's data, and bump the id so an in-flight
  // result can't land on the new key.
  const lastKeyRef = useRef(sKey);
  let current = state;
  if (lastKeyRef.current !== sKey) {
    lastKeyRef.current = sKey;
    fetchIdRef.current++;
    fetchingRef.current = false;
    current = readSnapshot<T>(client, key, false, null);
    setState(current);
  }

  const fetch = useCallback(async () => {
    const id = ++fetchIdRef.current;
    fetchingRef.current = true;
    setState(s => ({ ...s, isFetching: true }));

    try {
      await client.fetch(key, fetcherRef.current);
      if (fetchIdRef.current !== id) return;
      fetchingRef.current = false;
      // Read back from the cache, not the resolved value, so all observers of
      // the key converge on the same object.
      setState(s => readSnapshot<T>(client, key, false, s.error));
    } catch (err) {
      if (fetchIdRef.current !== id) return;
      fetchingRef.current = false;
      setState(s => ({ ...s, error: err as Error, isFetching: false, isLoading: false }));
    }
  }, [client, key]);

  useEffect(() => {
    const sync = () =>
      setState(s => readSnapshot<T>(client, key, fetchingRef.current, s.error));

    const unsub = client.subscribe(key, () => {
      sync();
      if (client.isStale(key, staleTime) && !fetchingRef.current) fetch();
    });

    // The cache may have changed between render and effect (or the key just changed).
    sync();
    if (client.isStale(key, staleTime)) fetch();

    return unsub;
  }, [client, key, fetch, staleTime]);

  // `current`, not `state`: on a key change the resynced value is correct now,
  // while `state` still holds the old key's data until React re-renders.
  return { ...current, fetch };
}

/**
 * Nothing is cached — use `onSuccess` with `client.invalidate` to refresh
 * affected queries. `mutate` resolves with the result and rethrows on failure.
 */
export function useMutation<TData, TVars = void, TResult = TData>(
  mutationFn: (vars: TVars, params: QueryFnParams) => Promise<TData>,
  options?: {
    onSuccess?: (params: { data: TData; vars: TVars; client: IQueryClient }) => void;
    onError?: (params: { error: Error; vars: TVars; client: IQueryClient }) => void;
    select?: (data: TData) => TResult;
  }
) {
  const client = useQueryClient();
  const [state, setState] = useState({ data: null as TResult | null, isLoading: false, error: null as Error | null });

  const mutationRef = useRef(mutationFn);
  mutationRef.current = mutationFn;
  const optionsRef = useRef(options);
  optionsRef.current = options;

  // Stable identity: consumers can safely put `mutate` in a dependency array.
  const mutate = useCallback(async (vars: TVars) => {
    setState(s => ({ ...s, isLoading: true, error: null }));
    try {
      const data = await client.execute((params) => mutationRef.current(vars, params));
      const opts = optionsRef.current;
      const transformedData = opts?.select ? opts.select(data) : (data as unknown as TResult);

      // State before onSuccess: callbacks usually invalidate, and the resulting
      // refetches should see this mutation already settled.
      setState({ data: transformedData, isLoading: false, error: null });
      opts?.onSuccess?.({ data, vars, client });
      return transformedData;
    } catch (err) {
      const error = err as Error;
      setState(s => ({ ...s, isLoading: false, error }));
      optionsRef.current?.onError?.({ error, vars, client });
      throw err;
    }
  }, [client]);

  return { ...state, mutate };
}
